import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  motion, AnimatePresence, useScroll, useTransform, useSpring, useInView, useVelocity,
} from 'framer-motion';
import Lenis from 'lenis';
import {
  Languages, Menu, X, ArrowUpRight, ArrowRight, MapPin, Linkedin, Github,
  ChevronUp, ChevronDown, Download, Copy, Check, Lock, ExternalLink,
} from 'lucide-react';
import { translations } from './translations';
import './Portfolio.mobile.css';

const reduced = () =>
  typeof window !== 'undefined' && window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const EASE = [0.22, 1, 0.36, 1];

const UI = {
  en: {
    details: 'Details', hide: 'Hide details', more: 'more', less: 'Show less',
    downloadCV: 'Download CV', visit: 'Visit', work: 'Selected work', liveSites: 'Live production sites',
    proof: 'Proof & links',
  },
  fr: {
    details: 'Détails', hide: 'Masquer', more: 'de plus', less: 'Réduire',
    downloadCV: 'Télécharger le CV', visit: 'Visiter', work: 'Travaux sélectionnés', liveSites: 'Sites en production',
    proof: 'Preuves & liens',
  },
};

const LOADER_KEY = 'portfolio_seen';
const EMAIL = 'Nizar.tazi@hotmail.fr';
const HERO_PORTRAIT = '/meall.jpeg';
const HERO_AVATAR = '/me_nobg.png';

/* Masked line-by-line heading reveal */
const MaskHeading = ({ lines, className, delay = 0, softLine }) => (
  <h1 className={className}>
    {lines.map((line, i) => (
      <span key={i} className="line-mask">
        <motion.span
          className={`block ${softLine === i ? 'text-soft' : ''}`}
          initial={{ y: '110%' }}
          animate={{ y: 0 }}
          transition={{ duration: 1, delay: delay + i * 0.1, ease: EASE }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </h1>
);

/* Rotating typewriter claim: turns a label into a proof */
const TypewriterClaim = ({ claims, className }) => {
  const [index, setIndex] = useState(0);
  const [text, setText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (reduced()) {
      setText(claims[0] || '');
      return undefined;
    }

    const current = claims[index] || '';
    let timeout;

    if (!isDeleting && text === current) {
      timeout = setTimeout(() => setIsDeleting(true), 2800);
    } else if (isDeleting && text === '') {
      setIsDeleting(false);
      setIndex((i) => (i + 1) % claims.length);
    } else {
      const speed = isDeleting ? 28 : 42;
      timeout = setTimeout(() => {
        setText(isDeleting ? current.slice(0, text.length - 1) : current.slice(0, text.length + 1));
      }, speed);
    }

    return () => clearTimeout(timeout);
  }, [text, isDeleting, index, claims]);

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.75, duration: 0.6, ease: EASE }}
      aria-live="polite"
    >
      <span className="font-mono text-[13px] leading-relaxed md:text-[15px]" style={{ color: 'var(--text-muted)' }}>
        <span style={{ color: 'var(--accent)' }} aria-hidden="true">› </span>
        {text}
        <span className="typewriter-cursor" aria-hidden="true">▌</span>
      </span>
    </motion.div>
  );
};

/* Subtle neural mesh: spatial anchor for the hero */
const HeroMesh = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext('2d');
    const staticFrame = reduced();
    const nodes = [];
    const NODE_COUNT = 48;
    const CONNECT_DIST = 165;
    const GRID = 56;
    const MAX_SPEED = 0.15;
    let animId = 0;
    let running = false;
    let w = 0;
    let h = 0;

    const capSpeed = (n) => {
      const speed = Math.hypot(n.vx, n.vy);
      if (speed > MAX_SPEED) {
        n.vx = (n.vx / speed) * MAX_SPEED;
        n.vy = (n.vy / speed) * MAX_SPEED;
      }
    };

    const seedNodes = () => {
      nodes.length = 0;
      for (let i = 0; i < NODE_COUNT; i += 1) {
        nodes.push({
          x: Math.random() * w,
          y: Math.random() * h,
          vx: (Math.random() - 0.5) * MAX_SPEED,
          vy: (Math.random() - 0.5) * MAX_SPEED,
        });
      }
    };

    const resize = (preserveNodes = false) => {
      const parent = canvas.parentElement;
      if (!parent) return;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const nextW = parent.offsetWidth;
      const nextH = parent.offsetHeight;

      if (nextW === w && nextH === h && nodes.length) return;

      w = nextW;
      h = nextH;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      if (!preserveNodes || !nodes.length) {
        seedNodes();
        return;
      }

      nodes.forEach((n) => {
        n.x = Math.min(w, Math.max(0, n.x));
        n.y = Math.min(h, Math.max(0, n.y));
        capSpeed(n);
      });
    };

    const drawGrid = () => {
      ctx.strokeStyle = 'rgba(255,255,255,0.07)';
      ctx.lineWidth = 1;
      for (let x = 0; x < w; x += GRID) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }
      for (let y = 0; y < h; y += GRID) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
      }
    };

    const drawFrame = () => {
      ctx.clearRect(0, 0, w, h);
      drawGrid();

      for (let i = 0; i < nodes.length; i += 1) {
        for (let j = i + 1; j < nodes.length; j += 1) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.hypot(dx, dy);
          if (dist < CONNECT_DIST) {
            const alpha = (1 - dist / CONNECT_DIST) * 0.28;
            ctx.strokeStyle = `rgba(139, 128, 255, ${alpha})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }

      nodes.forEach((n) => {
        ctx.shadowBlur = 10;
        ctx.shadowColor = 'rgba(139, 128, 255, 0.45)';
        ctx.fillStyle = 'rgba(139, 128, 255, 0.55)';
        ctx.beginPath();
        ctx.arc(n.x, n.y, 2.4, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;

        if (!staticFrame) {
          n.x += n.vx;
          n.y += n.vy;
          if (n.x <= 0 || n.x >= w) {
            n.vx *= -1;
            n.x = Math.min(w, Math.max(0, n.x));
          }
          if (n.y <= 0 || n.y >= h) {
            n.vy *= -1;
            n.y = Math.min(h, Math.max(0, n.y));
          }
          capSpeed(n);
        }
      });
    };

    const loop = () => {
      if (!running) return;
      drawFrame();
      animId = requestAnimationFrame(loop);
    };

    const start = () => {
      if (staticFrame || running) return;
      running = true;
      animId = requestAnimationFrame(loop);
    };

    const stop = () => {
      running = false;
      cancelAnimationFrame(animId);
      animId = 0;
    };

    resize(false);
    drawFrame();
    start();

    let resizeTimer;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => resize(true), 120);
    };

    const onVisibility = () => {
      if (document.hidden) {
        stop();
        return;
      }
      resize(true);
      drawFrame();
      start();
    };

    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return <canvas ref={canvasRef} className="hero-mesh" aria-hidden="true" />;
};

/* Click-to-copy email */
const CopyEmail = ({ email, copyLabel, copiedLabel }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.location.href = `mailto:${email}`;
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="group text-left"
      aria-label={copied ? copiedLabel : copyLabel}
    >
      <span className="link-underline inline-flex items-center gap-3 font-display text-2xl font-semibold tracking-tight text-white transition-colors group-hover:text-white/80 md:text-4xl">
        {email}
        {copied
          ? <Check className="h-6 w-6 md:h-8 md:w-8" style={{ color: '#4ade80' }} />
          : <Copy className="h-5 w-5 opacity-0 transition-opacity group-hover:opacity-60 md:h-6 md:w-6" style={{ color: 'var(--text-faint)' }} />}
        {!copied && <ArrowUpRight className="h-6 w-6 md:h-8 md:w-8" />}
      </span>
      <span className="mt-2 block font-mono text-[11px] uppercase tracking-[0.14em]" style={{ color: copied ? '#4ade80' : 'var(--text-faint)' }}>
        {copied ? copiedLabel : copyLabel}
      </span>
    </button>
  );
};

/* Project proof badge / link */
const ProofBadge = ({ item }) => {
  if (item.type === 'nda') {
    return (
      <span className="proof-badge proof-badge-nda">
        <Lock className="h-3 w-3" />{item.label}
      </span>
    );
  }
  const Icon = item.type === 'github' ? Github : ExternalLink;
  return (
    <a href={item.url} target="_blank" rel="noopener noreferrer" className="proof-badge proof-badge-link">
      <Icon className="h-3 w-3" />{item.label}<ArrowUpRight className="h-3 w-3" />
    </a>
  );
};

/* Count-up */
const Counter = ({ value, className, style }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '0px 0px -10% 0px' });
  const num = parseInt(value, 10) || 0;
  const suffix = value.slice(String(num).length);
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!inView) return;
    if (reduced()) { setDisplay(num); return; }
    let raf;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / 1300);
      setDisplay(Math.round((1 - Math.pow(1 - p, 3)) * num));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, num]);
  return <span ref={ref} className={className} style={style}>{display}{suffix}</span>;
};

/* Fade + rise reveal */
const Reveal = ({ children, className, delay = 0, y = 24, style, as }) => {
  const Comp = motion[as || 'div'];
  return (
    <Comp
      className={className}
      style={style}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '0px 0px -12% 0px' }}
      transition={{ duration: 0.8, delay, ease: EASE }}
    >
      {children}
    </Comp>
  );
};

/* Section label + masked title */
const SectionHeader = ({ index, title, kicker }) => (
  <div className="mb-16 md:mb-20">
    <Reveal className="mb-5 flex items-center gap-4">
      <span className="font-mono text-xs tracking-widest" style={{ color: 'var(--accent)' }}>/{index}</span>
      {kicker && <span className="font-mono text-xs uppercase tracking-[0.22em]" style={{ color: 'var(--text-faint)' }}>{kicker}</span>}
    </Reveal>
    <span className="line-mask">
      <motion.h2
        className="font-display text-[2rem] font-semibold leading-[1.05] tracking-tight text-white md:text-[3.25rem]"
        initial={{ y: '110%' }}
        whileInView={{ y: 0 }}
        viewport={{ once: true, margin: '0px 0px -10% 0px' }}
        transition={{ duration: 0.9, ease: EASE }}
      >
        {title}
      </motion.h2>
    </span>
  </div>
);

/* ----- Project item with case-study framing ----- */
const ProjectItem = ({ p, labels, ui }) => {
  const [open, setOpen] = useState(false);
  return (
    <Reveal className="hair py-14 first:border-t-0 first:pt-0 md:py-20">
      <div className="grid gap-8 md:grid-cols-12 md:gap-10">
        <div className="md:col-span-5">
          <div className="flex items-baseline gap-4">
            <span className="font-mono text-sm" style={{ color: 'var(--accent)' }}>{String(p.id).padStart(2, '0')}</span>
            <span className="font-mono text-[11px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-faint)' }}>{p.category}</span>
          </div>
          <h3 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight text-white md:text-[2.5rem]">{p.title}</h3>
          <div className="mt-3 font-mono text-xs" style={{ color: 'var(--text-faint)' }}>{p.year}</div>
          <div className="mt-8 flex flex-wrap gap-x-10 gap-y-4">
            {p.metrics.map((m, k) => (
              <div key={k}>
                <div className="font-display text-xl font-semibold text-white">{m.value}</div>
                <div className="mt-0.5 text-xs" style={{ color: 'var(--text-faint)' }}>{m.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-7">
          <div className="case-study grid gap-5">
            {[
              { key: 'problem', label: labels.problem, text: p.problem },
              { key: 'built', label: labels.built, text: p.built },
              { key: 'outcome', label: labels.outcome, text: p.outcome },
            ].map((block) => (
              <div key={block.key}>
                <div className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--accent)' }}>{block.label}</div>
                <p className="text-[15px] leading-relaxed md:text-base" style={{ color: 'var(--text-muted)' }}>{block.text}</p>
              </div>
            ))}
          </div>

          {p.shots && (
            <div className="mt-7">
              <div className="mb-3 font-mono text-[11px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-faint)' }}>{ui.liveSites}</div>
              <div className="grid gap-4 sm:grid-cols-2">
                {p.shots.map((s, k) => (
                  <a key={k} href={s.url} target="_blank" rel="noopener noreferrer" className="shot group block">
                    <span className="shot-bar">
                      <i /><i /><i />
                      <span className="ml-2 truncate font-mono text-[10px]" style={{ color: 'var(--text-faint)' }}>{s.url.replace('https://', '')}</span>
                    </span>
                    <span className="block aspect-[16/10] overflow-hidden">
                      <img src={`/${s.shot}`} alt={s.label} loading="lazy" />
                    </span>
                    <span className="flex items-center justify-between px-3 py-2.5">
                      <span className="text-sm font-semibold text-white">{s.label}</span>
                      <span className="flex items-center gap-1 text-[11px] font-medium" style={{ color: 'var(--accent)' }}>{ui.visit}<ArrowUpRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
                    </span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {p.proof && p.proof.length > 0 && (
            <div className="mt-7">
              <div className="mb-3 font-mono text-[11px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-faint)' }}>{ui.proof}</div>
              <div className="flex flex-wrap gap-2">
                {p.proof.map((item, k) => <ProofBadge key={k} item={item} />)}
              </div>
            </div>
          )}

          <div className="mt-7 flex flex-wrap gap-x-5 gap-y-2">
            {p.stack.map((tech, k) => <span key={k} className="tag">{tech}</span>)}
          </div>

          <button onClick={() => setOpen((v) => !v)} className="mt-7 inline-flex items-center gap-1.5 text-[13px] font-semibold text-white">
            {open ? ui.hide : ui.details}
            <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
          </button>
          <AnimatePresence initial={false}>
            {open && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4, ease: EASE }} className="overflow-hidden">
                <ul className="mt-5 grid gap-x-8 gap-y-2.5 sm:grid-cols-2">
                  {p.highlights.map((h, k) => (
                    <li key={k} className="flex items-start gap-3 text-sm" style={{ color: 'var(--text-muted)' }}>
                      <span className="mt-2.5 h-px w-3 flex-shrink-0" style={{ background: 'var(--text-faint)' }} />{h}
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </Reveal>
  );
};

/* Experience item: current role fully expanded */
const ExperienceItem = ({ exp, current, currentLabel, ui }) => {
  const previewCount = current ? exp.achievements.length : 2;
  const [open, setOpen] = useState(current);
  const visible = open ? exp.achievements : exp.achievements.slice(0, previewCount);
  const hidden = exp.achievements.length - previewCount;
  return (
    <Reveal className="hair py-12 first:border-t-0 first:pt-0">
      <div className="grid gap-6 md:grid-cols-12 md:gap-10">
        <div className="md:col-span-4">
          <div className="flex items-center gap-3">
            <span className={`grid h-10 ${exp.logoWide ? 'w-28' : 'w-10'} flex-shrink-0 place-items-center overflow-hidden rounded-lg`} style={{ background: exp.logoBg }}>
              <img src={`/${exp.logo}`} alt={exp.company} className="h-full w-full object-contain p-1.5" onError={(e) => { e.target.style.display = 'none'; }} />
            </span>
            <div>
              {!exp.logoWide && <div className="font-display text-base font-semibold text-white">{exp.company}</div>}
              <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--text-faint)' }}><MapPin className="h-3 w-3" />{exp.location}</div>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 font-mono text-xs" style={{ color: current ? '#4ade80' : 'var(--text-faint)' }}>
            {current && <span className="h-1.5 w-1.5 rounded-full" style={{ background: '#4ade80' }} />}
            {current ? `${currentLabel} · ${exp.period}` : exp.period}
          </div>
        </div>
        <div className="md:col-span-8">
          <h3 className="font-display text-xl font-semibold tracking-tight text-white md:text-2xl">{exp.title}</h3>
          <ul className="mt-5 space-y-2.5">
            {visible.map((a, i) => (
              <li key={i} className="flex items-start gap-3 text-sm leading-relaxed md:text-[15px]" style={{ color: 'var(--text-muted)' }}>
                <span className="mt-2.5 h-px w-3 flex-shrink-0" style={{ background: 'var(--text-faint)' }} />{a}
              </li>
            ))}
          </ul>
          {hidden > 0 && (
            <button onClick={() => setOpen((v) => !v)} className="mt-4 text-[13px] font-semibold text-white">
              {open ? ui.less : `+${hidden} ${ui.more}`}
            </button>
          )}
        </div>
      </div>
    </Reveal>
  );
};

/* ================================================================== */
const Portfolio = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const [isScrolled, setIsScrolled] = useState(false);
  const [language, setLanguage] = useState('en');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);
  const [loading, setLoading] = useState(() => {
    if (reduced()) return false;
    try {
      return !sessionStorage.getItem(LOADER_KEY);
    } catch {
      return false;
    }
  });

  const lenisRef = useRef(null);
  const mouseGlowRef = useRef(null);
  const mouseRingRef = useRef(null);
  const t = translations[language];
  const ui = UI[language];

  /* custom cursor: soft glow trailing a precise ring */
  useEffect(() => {
    if (reduced()) return;
    if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return;
    const glow = mouseGlowRef.current;
    const ring = mouseRingRef.current;
    if (!glow || !ring) return;

    const GLOW_HALF = 160;
    const RING_HALF = 40;
    let tx = window.innerWidth / 2;
    let ty = window.innerHeight / 2;
    let gx = tx, gy = ty;
    let rx = tx, ry = ty;
    let raf;

    const show = () => {
      if (glow.style.opacity !== '1') { glow.style.opacity = '1'; ring.style.opacity = '1'; }
    };
    const onMove = (e) => { tx = e.clientX; ty = e.clientY; show(); };
    const onLeave = () => { glow.style.opacity = '0'; ring.style.opacity = '0'; };

    const interactive = 'a, button, [role="button"], input, textarea, select, label';
    const onOver = (e) => { if (e.target.closest && e.target.closest(interactive)) ring.classList.add('cursor-ring-hover'); };
    const onOut = (e) => { if (e.target.closest && e.target.closest(interactive)) ring.classList.remove('cursor-ring-hover'); };

    const loop = () => {
      gx += (tx - gx) * 0.1;  gy += (ty - gy) * 0.1;
      rx += (tx - rx) * 0.3;  ry += (ty - ry) * 0.3;
      glow.style.transform = `translate3d(${gx - GLOW_HALF}px, ${gy - GLOW_HALF}px, 0)`;
      ring.style.transform = `translate3d(${rx - RING_HALF}px, ${ry - RING_HALF}px, 0)`;
      raf = requestAnimationFrame(loop);
    };

    document.documentElement.classList.add('custom-cursor');
    window.addEventListener('mousemove', onMove, { passive: true });
    document.addEventListener('mouseleave', onLeave);
    document.addEventListener('mouseover', onOver, { passive: true });
    document.addEventListener('mouseout', onOut, { passive: true });
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      document.documentElement.classList.remove('custom-cursor');
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', onLeave);
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseout', onOut);
    };
  }, []);

  useEffect(() => {
    if (reduced()) return;
    const lenis = new Lenis({ duration: 1.15, easing: (x) => Math.min(1, 1.001 - Math.pow(2, -10 * x)), smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); lenisRef.current = null; };
  }, []);

  /* loader: first visit only, skippable */
  useEffect(() => {
    if (!loading) {
      lenisRef.current?.start();
      document.body.style.overflow = '';
      return;
    }
    lenisRef.current?.stop();
    document.body.style.overflow = 'hidden';
    const dismiss = () => {
      try { sessionStorage.setItem(LOADER_KEY, '1'); } catch { /* ignore */ }
      setLoading(false);
    };
    const timer = setTimeout(dismiss, 1300);
    window.addEventListener('wheel', dismiss, { passive: true, once: true });
    window.addEventListener('touchstart', dismiss, { passive: true, once: true });
    window.addEventListener('keydown', dismiss, { once: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener('wheel', dismiss);
      window.removeEventListener('touchstart', dismiss);
      window.removeEventListener('keydown', dismiss);
    };
  }, [loading]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 20);
      setShowTop(window.scrollY > 700);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length) {
          const top = visible.reduce((p, c) => (c.intersectionRatio > p.intersectionRatio ? c : p));
          setActiveSection(top.target.id);
        }
      },
      { threshold: [0, 0.25, 0.5], rootMargin: '-45% 0px -45% 0px' }
    );
    const sections = document.querySelectorAll('section[id]');
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const navTo = useCallback((id) => (e) => {
    if (e) e.preventDefault();
    setMobileMenuOpen(false);
    const target = id === 'hero' ? 0 : document.getElementById(id);
    if (lenisRef.current) {
      lenisRef.current.scrollTo(target, { offset: id === 'hero' ? 0 : -70, duration: 1.3 });
    } else if (target !== null) {
      const y = target === 0 ? 0 : target.getBoundingClientRect().top + window.scrollY - 70;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    }
  }, []);

  const { scrollYProgress } = useScroll();
  const progressX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.4 });

  const heroRef = useRef(null);
  const { scrollYProgress: heroProg } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroFade = useTransform(heroProg, [0, 0.7], [1, 0]);
  const heroY = useTransform(heroProg, [0, 1], [0, 100]);

  const scrollVel = useVelocity(scrollYProgress);
  const skewRaw = useTransform(scrollVel, [-3, 0, 3], [-6, 0, 6], { clamp: true });
  const skew = useSpring(skewRaw, { stiffness: 200, damping: 40 });

  const navItems = [
    { id: 'about', label: t.nav.about },
    { id: 'projects', label: t.nav.projects },
    { id: 'experience', label: t.nav.experience },
    { id: 'skills', label: t.nav.skills },
    { id: 'contact', label: t.nav.contact },
  ];
  const railItems = [{ id: 'hero' }, ...navItems];

  const stats = [
    { value: '60%', labelKey: 'efficiencyGain', contextKey: 'efficiencyContext' },
    { value: '500+', labelKey: 'activeUsers', contextKey: 'activeUsersContext' },
    { value: '85%+', labelKey: 'mlAccuracy', contextKey: 'mlAccuracyContext' },
    { value: '40%+', labelKey: 'costReduction', contextKey: 'costReductionContext' },
  ];

  const marquee = ['Python', 'LangChain', 'LangGraph', 'OpenAI', 'Claude', 'React', 'Node.js', 'TypeScript', 'TensorFlow', 'PyTorch', 'MLflow', 'Docker', 'Azure', 'Stripe', 'PostgreSQL', 'Power BI', 'Spark', 'Kubernetes'];

  const credentials = [
    { value: t.about.mastersDegrees, lines: [t.about.mlsd, t.about.bigData] },
    { value: t.about.yearsExperience, lines: [t.about.professionalExperience] },
    { value: t.about.modelsDeployed, lines: [t.about.deployedInProduction] },
  ];

  const projects = t.projects.items.map((proj, index) => ({
    id: index + 1, ...proj,
    year: index === 0 ? '2024-2025' : '2024',
    stack: index === 0 ? ['React', 'Node.js', 'UI/UX', 'Payment API', 'Calendar API']
      : index === 1 ? ['Python', 'LangChain', 'LangGraph', 'OpenAI', 'Claude', 'Railway', 'Docker']
      : index === 2 ? ['React', 'Node.js', 'PostgreSQL', 'Stripe', 'SSO', 'TypeScript']
      : ['Python', 'MLflow', 'Scikit-learn', 'TensorFlow', 'Azure', 'Spark'],
    shots: index === 0 ? [
      { url: 'https://sonorave-rentals.com', label: 'Sonorave Rentals', shot: 'sonorave_screenshot.png' },
      { url: 'https://banelhaqdental.com', label: 'Dr Ayoub Banelhaq', shot: 'banelhaqdental_screenshot.png' },
    ] : undefined,
  }));

  const skillTiers = [
    { key: 'production', label: t.skills.production, desc: t.skills.productionDesc },
    { key: 'comfortable', label: t.skills.comfortable, desc: t.skills.comfortableDesc },
    { key: 'exploring', label: t.skills.exploring, desc: t.skills.exploringDesc },
  ];

  const experiences = t.experience.items.map((exp, index) => ({
    ...exp,
    company: index === 0 ? 'Applause' : index === 1 ? 'Edpulse' : index === 2 ? 'Arhs Development' : 'Sanofi',
    current: index === 0,
    logo: index === 0 ? 'applause.png' : index === 1 ? 'edpulse.png' : index === 2 ? 'arhs.png' : 'sanofi.png',
    logoBg: index === 0 ? '#ffffff' : index === 1 ? '#053046' : index === 2 ? '#00a761' : '#ffffff',
    logoWide: index === 0,
  }));

  return (
    <div className="grain relative min-h-screen overflow-x-hidden" style={{ background: 'var(--bg)', color: 'var(--text)' }}>
      <AnimatePresence>{loading && <Loader name="Nizar Tazi" tagline={t.hero.tagline} onSkip={() => { try { sessionStorage.setItem(LOADER_KEY, '1'); } catch { /* ignore */ } setLoading(false); }} />}</AnimatePresence>

      {/* progress bar */}
      <motion.div className="fixed left-0 top-0 z-[9999] h-px w-full origin-left" style={{ scaleX: progressX, background: 'rgba(255,255,255,0.5)' }} />

      {/* one faint ambient glow */}
      <div className="pointer-events-none fixed inset-0 z-0">
        <div className="glow" style={{ width: 600, height: 600, top: -260, left: '20%', background: 'rgba(139,128,255,0.10)' }} />
      </div>

      {/* cursor glow + ring */}
      <div
        ref={mouseGlowRef}
        className="pointer-events-none fixed left-0 top-0 z-0 hidden md:block"
        style={{
          width: 320,
          height: 320,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139,128,255,0.11) 0%, rgba(139,128,255,0.04) 35%, transparent 65%)',
          filter: 'blur(14px)',
          opacity: 0,
          transition: 'opacity 0.45s ease',
          willChange: 'transform',
        }}
      />
      <div
        ref={mouseRingRef}
        className="cursor-ring-wrap pointer-events-none fixed left-0 top-0 hidden md:grid"
        style={{ opacity: 0, transition: 'opacity 0.4s ease', willChange: 'transform', zIndex: 99999 }}
      >
        <span className="cursor-ring" />
      </div>

      {/* side rail */}
      <div className="fixed right-7 top-1/2 z-[60] hidden -translate-y-1/2 flex-col items-center gap-3.5 lg:flex">
        {railItems.map((item, index) => (
          <button
            key={item.id}
            onClick={navTo(item.id)}
            aria-label={`Go to ${item.id}`}
            className="transition-transform duration-300"
            style={{ transform: `translateX(${index % 2 === 0 ? '-7px' : '7px'})` }}
          >
            <span className={`rail-dot block ${activeSection === item.id ? 'active' : ''}`} />
          </button>
        ))}
      </div>

      {/* NAV */}
      <nav className="fixed left-0 right-0 top-0 z-[9998] transition-all duration-500" style={{ background: isScrolled ? 'rgba(7,7,10,0.6)' : 'transparent', backdropFilter: isScrolled ? 'blur(14px)' : 'none', WebkitBackdropFilter: isScrolled ? 'blur(14px)' : 'none' }}>
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 md:px-8 md:py-5">
          <button onClick={navTo('hero')} className="font-display text-[15px] font-semibold tracking-tight text-white">Nizar Tazi<span style={{ color: 'var(--accent)' }}>.</span></button>

          <div className="hidden items-center gap-7 md:flex">
            {navItems.map((item) => (
              <button key={item.id} onClick={navTo(item.id)} className="link-underline text-[13px] font-medium transition-colors" style={{ color: activeSection === item.id ? '#fff' : 'var(--text-muted)' }}>
                {item.label}
              </button>
            ))}
          </div>

          <div className="hidden items-center gap-5 md:flex">
            <div className="relative">
              <button onClick={() => setLangOpen((v) => !v)} className="flex items-center gap-1.5 text-[13px] font-medium transition-colors hover:text-white" style={{ color: 'var(--text-muted)' }}>
                <Languages className="h-4 w-4" />{language.toUpperCase()}
              </button>
              {langOpen && (
                <>
                  <div className="fixed inset-0 z-[1]" onClick={() => setLangOpen(false)} />
                  <div className="absolute right-0 top-full z-[2] mt-3 w-32 overflow-hidden rounded-lg" style={{ background: 'var(--panel-2)' }}>
                    {['en', 'fr'].map((lng) => (
                      <button key={lng} onClick={() => { setLanguage(lng); setLangOpen(false); }} className="block w-full px-4 py-2.5 text-left text-[13px] transition-colors hover:bg-white/5" style={{ color: language === lng ? '#fff' : 'var(--text-muted)' }}>
                        {lng === 'en' ? 'English' : 'Français'}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <Link to="/CV" className="group flex items-center gap-1.5 text-[13px] font-semibold text-white">
              {t.nav.viewCV}<ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            {isScrolled && (
              <button onClick={navTo('contact')} className="rounded-full bg-white px-4 py-2 text-[12px] font-semibold text-black transition-transform hover:scale-[1.03]">
                {t.hero.getInTouch}
              </button>
            )}
          </div>

          <div className="flex items-center gap-4 md:hidden">
            <Link to="/CV" className="text-[13px] font-semibold text-white">{t.nav.viewCV}</Link>
            <button onClick={() => setMobileMenuOpen((v) => !v)} className="text-white" aria-label="Menu">
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* mobile menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div className="fixed inset-0 z-[9997] md:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setMobileMenuOpen(false)} style={{ background: 'rgba(7,7,10,0.96)' }}>
            <div className="flex h-full flex-col justify-center px-8" onClick={(e) => e.stopPropagation()}>
              {navItems.map((item, i) => (
                <motion.button key={item.id} onClick={navTo(item.id)} className="border-b py-5 text-left font-display text-3xl font-semibold text-white" style={{ borderColor: 'var(--line)' }} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}>
                  {item.label}
                </motion.button>
              ))}
              <a href={`/CV_Nizar_Tazi_${language}.pdf`} download className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-white"><Download className="h-4 w-4" />{ui.downloadCV}</a>
              <div className="mt-6 flex gap-3">
                {['en', 'fr'].map((lng) => (
                  <button key={lng} onClick={() => setLanguage(lng)} className="text-sm font-medium" style={{ color: language === lng ? '#fff' : 'var(--text-faint)' }}>{lng.toUpperCase()}</button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* HERO */}
      <section id="hero" ref={heroRef} className="relative z-10 flex flex-col overflow-x-hidden lg:min-h-[100svh] lg:overflow-hidden">
        <div className="hero-mesh-wrap pointer-events-none absolute inset-0">
          <HeroMesh />
          <div className="hero-mesh-fade" />
        </div>

        <motion.div className="hero-content relative z-[2] mx-auto flex w-full max-w-6xl flex-col justify-start px-5 pt-24 pb-2 sm:px-6 sm:pt-28 md:px-8 md:pt-48 md:pb-6 lg:flex-1 lg:pb-8" style={{ opacity: heroFade, y: heroY }}>
          <div className="hero-grid grid items-start gap-8 md:grid-cols-[1.25fr_0.75fr] md:gap-x-14 md:gap-y-10">
            <div className="hero-copy-top flex flex-col gap-6 md:gap-0">
              <motion.div className="md:mb-8 flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:gap-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 0.6 }}>
                <span className="inline-flex w-fit items-center gap-2 rounded-full px-3 py-1.5 text-[13px]" style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--text-muted)' }}>
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" style={{ background: '#4ade80' }} />
                    <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: '#4ade80' }} />
                  </span>
                  {t.hero.availability}
                </span>
                <span className="max-w-md text-[13px] leading-snug" style={{ color: 'var(--text-faint)' }}>
                  {t.hero.tagline}
                </span>
              </motion.div>

              <motion.div
                className="hero-avatar flex justify-center md:hidden"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.42, duration: 0.55, ease: EASE }}
              >
                <img src={HERO_AVATAR} alt="Nizar Tazi" className="hero-avatar-img" draggable={false} />
              </motion.div>

              <MaskHeading
                className="font-display text-[2.5rem] font-semibold leading-[0.98] tracking-[-0.02em] text-white sm:text-[2.85rem] md:text-[5.25rem]"
                lines={[t.hero.title, t.hero.titleHighlight]}
                softLine={1}
                delay={0.35}
              />

              <TypewriterClaim key={language} claims={t.hero.claims} className="min-h-[1.6rem] md:mt-6 md:min-h-[1.75rem]" />
            </div>

            <motion.div className="hero-portrait hidden justify-end md:flex">
              <motion.div
                className="relative w-full max-w-[330px]"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6, duration: 1, ease: EASE }}
              >
                <div className="overflow-hidden rounded-2xl ring-1 ring-white/10">
                  <img src={HERO_PORTRAIT} alt="Nizar Tazi" className="block h-auto w-full object-cover grayscale-[15%]" draggable={false} />
                </div>
              </motion.div>
            </motion.div>

            <div className="hero-copy-bottom flex flex-col gap-6 md:gap-8">
              <motion.p className="max-w-lg text-[15px] leading-relaxed sm:text-base md:text-lg" style={{ color: 'var(--text-muted)' }} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.7 }}>
                {t.hero.description}
              </motion.p>

              <motion.p
                className="font-mono text-[10px] uppercase leading-relaxed tracking-[0.12em] sm:text-[11px] md:text-xs md:tracking-[0.16em]"
                style={{ color: 'var(--text-faint)' }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.6 }}
              >
                {t.hero.proofHook}
              </motion.p>

              <motion.div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-center md:gap-x-8 md:gap-y-3" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.7 }}>
                <button onClick={navTo('projects')} className="group flex w-full items-center justify-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-black transition-transform hover:scale-[1.03] md:w-auto">
                  {t.hero.viewProjects}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
                <a href={`/CV_Nizar_Tazi_${language}.pdf`} download className="group flex items-center justify-center gap-2 text-sm font-semibold text-white md:justify-start">
                  <Download className="h-4 w-4" /><span className="link-underline">{ui.downloadCV}</span>
                </a>
                <button onClick={navTo('contact')} className="link-underline text-center text-sm font-semibold md:text-left" style={{ color: 'var(--text-muted)' }}>{t.hero.getInTouch}</button>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* MARQUEE */}
        <div className="hero-marquee relative z-10 mt-10 shrink-0 overflow-hidden py-5 hair md:mt-12 md:py-6 lg:mt-auto">
          <motion.div style={{ skewX: skew }}>
            <div className="marquee-track items-center">
              {[...marquee, ...marquee].map((tech, i) => (
                <span key={i} className="flex items-center">
                  <span className="tag px-6">{tech}</span>
                  <span className="h-1 w-1 rounded-full" style={{ background: 'var(--text-faint)' }} />
                </span>
              ))}
            </div>
          </motion.div>
          <div className="pointer-events-none absolute inset-y-0 left-0 w-28" style={{ background: 'linear-gradient(90deg,var(--bg),transparent)' }} />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-28" style={{ background: 'linear-gradient(270deg,var(--bg),transparent)' }} />
        </div>
      </section>

      {/* Hero stats */}
      <div className="relative z-10 mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-20">
        <div className="grid grid-cols-2 gap-y-8 md:grid-cols-4">
          {stats.map((s, i) => (
            <motion.div key={i} className={i % 4 !== 0 ? 'md:border-l md:pl-8' : 'md:pl-0'} style={{ borderColor: 'var(--line)' }} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '0px 0px -10% 0px' }} transition={{ delay: i * 0.08, duration: 0.6 }}>
              <Counter value={s.value} className="font-display text-4xl font-semibold tracking-tight text-white md:text-5xl" />
              <div className="mt-2 text-[13px]" style={{ color: 'var(--text-faint)' }}>{t.hero.stats[s.labelKey]}</div>
              <div className="mt-1 font-mono text-[11px]" style={{ color: 'var(--text-faint)', opacity: 0.75 }}>{t.hero.stats[s.contextKey]}</div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ABOUT */}
      <section id="about" className="relative z-10 mx-auto max-w-6xl px-5 py-28 md:px-8 md:py-40">
        <SectionHeader index="01" kicker={t.nav.about} title={t.about.title} />
        <div className="max-w-3xl">
          <Reveal>
            <p className="font-display text-xl font-medium leading-[1.5] tracking-tight text-white md:text-[1.75rem]">{t.about.lead}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <ul className="mt-8 space-y-3">
              {t.about.bullets.map((bullet, i) => (
                <li key={i} className="flex items-start gap-3 text-base leading-relaxed md:text-lg" style={{ color: 'var(--text-muted)' }}>
                  <span className="mt-2.5 h-px w-4 flex-shrink-0" style={{ background: 'var(--accent)' }} />
                  {bullet}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        <div className="mt-20 grid gap-y-10 md:grid-cols-3">
          {credentials.map((c, i) => (
            <Reveal key={i} delay={i * 0.08} className={i !== 0 ? 'md:border-l md:pl-10' : ''} style={{ borderColor: 'var(--line)' }}>
              <div className="font-display text-3xl font-semibold tracking-tight text-white md:text-4xl">{c.value}</div>
              <div className="mt-3 space-y-0.5">
                {c.lines.map((l, j) => <div key={j} className="text-sm" style={{ color: 'var(--text-faint)' }}>{l}</div>)}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PROJECTS */}
      <section id="projects" className="relative z-10 mx-auto max-w-6xl px-5 py-28 md:px-8 md:py-40">
        <SectionHeader index="02" kicker={ui.work} title={t.projects.title} />
        <div>
          {projects.map((p) => <ProjectItem key={p.id} p={p} labels={t.projects} ui={ui} />)}
        </div>
      </section>

      {/* EXPERIENCE */}
      <section id="experience" className="relative z-10 mx-auto max-w-6xl px-5 py-28 md:px-8 md:py-40">
        <SectionHeader index="03" kicker={t.nav.experience} title={t.experience.title} />
        <div>
          {experiences.map((exp, index) => (
            <ExperienceItem key={index} exp={exp} current={exp.current} currentLabel={t.experience.current} ui={ui} />
          ))}
        </div>
      </section>

      {/* SKILLS */}
      <section id="skills" className="relative z-10 mx-auto max-w-6xl px-5 py-28 md:px-8 md:py-40">
        <SectionHeader index="04" kicker={t.nav.skills} title={t.skills.title} />
        <div>
          {skillTiers.map((tier, i) => (
            <Reveal key={tier.key} delay={i * 0.05} className="hair grid gap-4 py-9 first:border-t-0 first:pt-0 md:grid-cols-12 md:gap-10">
              <div className="md:col-span-4">
                <h3 className="font-display text-lg font-semibold text-white">{tier.label}</h3>
                <p className="mt-1 text-sm" style={{ color: 'var(--text-faint)' }}>{tier.desc}</p>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-4 md:col-span-8">
                {t.skills.tiers[tier.key].map((skill, j) => (
                  <span key={j} className="skill-chip">
                    <span className="skill-chip-name">{skill.name}</span>
                    <span className="skill-chip-context">{skill.context}</span>
                  </span>
                ))}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="relative z-10 mx-auto max-w-6xl px-5 py-28 md:px-8 md:py-44">
        <Reveal>
          <div className="font-mono text-xs uppercase tracking-[0.22em]" style={{ color: 'var(--text-faint)' }}>{t.nav.contact}</div>
          <h2 className="mt-6 max-w-4xl font-display text-[2.5rem] font-semibold leading-[1.02] tracking-[-0.02em] text-white md:text-[5rem]">{t.contact.title}</h2>
          <p className="mt-7 max-w-xl text-base leading-relaxed md:text-lg" style={{ color: 'var(--text-muted)' }}>{t.contact.subtitle}</p>
        </Reveal>

        <Reveal delay={0.08} className="mt-16 max-w-3xl">
          <blockquote className="testimonial">
            <p className="text-lg leading-relaxed md:text-xl" style={{ color: 'var(--text-muted)' }}>&ldquo;{t.testimonial.quote}&rdquo;</p>
            <footer className="mt-5 flex items-center gap-3">
              <span className="grid h-9 w-9 place-items-center rounded-full text-xs font-semibold text-white" style={{ background: 'var(--panel-2)' }}>TL</span>
              <div>
                <div className="text-sm font-semibold text-white">{t.testimonial.author}</div>
                <div className="text-xs" style={{ color: 'var(--text-faint)' }}>{t.testimonial.company}</div>
              </div>
            </footer>
          </blockquote>
        </Reveal>

        <Reveal delay={0.1} className="mt-14">
          <CopyEmail email={EMAIL} copyLabel={t.contact.copyEmail} copiedLabel={t.contact.copied} />
          <div className="mt-12 grid gap-y-8 md:grid-cols-3">
            <div>
              <div className="text-xs uppercase tracking-wider" style={{ color: 'var(--text-faint)' }}>{t.contact.phone}</div>
              <a href="tel:+212779168753" className="mt-2 block text-base text-white transition-colors hover:text-white/70">+212779168753</a>
            </div>
            <div className="md:border-l md:pl-10" style={{ borderColor: 'var(--line)' }}>
              <div className="text-xs uppercase tracking-wider" style={{ color: 'var(--text-faint)' }}>{t.contact.location}</div>
              <div className="mt-2 text-base text-white">Tanger, Maroc</div>
            </div>
            <div className="md:border-l md:pl-10" style={{ borderColor: 'var(--line)' }}>
              <div className="text-xs uppercase tracking-wider" style={{ color: 'var(--text-faint)' }}>Social</div>
              <div className="mt-2 flex gap-5">
                <a href="https://linkedin.com/in/nizar-tazi/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-base text-white transition-colors hover:text-white/70"><Linkedin className="h-4 w-4" />LinkedIn</a>
                <a href="https://github.com/NizarTazi/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-base text-white transition-colors hover:text-white/70"><Github className="h-4 w-4" />GitHub</a>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* FOOTER */}
      <footer className="relative z-10 mx-auto max-w-6xl px-5 py-10 hair md:px-8">
        <div className="flex flex-col items-center justify-between gap-3 text-xs sm:flex-row" style={{ color: 'var(--text-faint)' }}>
          <span>© {new Date().getFullYear()} Nizar Tazi</span>
          <span className="font-mono">{t.hero.tagline}</span>
        </div>
      </footer>

      {/* Mobile sticky CTA bar */}
      <AnimatePresence>
        {isScrolled && (
          <motion.div
            className="fixed bottom-0 left-0 right-0 z-[9996] flex gap-3 border-t px-4 py-3 md:hidden"
            style={{ background: 'rgba(7,7,10,0.92)', borderColor: 'var(--line)', backdropFilter: 'blur(14px)' }}
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
          >
            <a href={`/CV_Nizar_Tazi_${language}.pdf`} download className="flex flex-1 items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold text-white ring-1 ring-white/15">
              <Download className="h-4 w-4" />{ui.downloadCV}
            </a>
            <button onClick={navTo('contact')} className="flex flex-1 items-center justify-center rounded-full bg-white py-3 text-sm font-semibold text-black">
              {t.hero.getInTouch}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* WhatsApp */}
      <a href="https://wa.me/212779168753" target="_blank" rel="noopener noreferrer" className="fixed bottom-24 right-6 z-50 grid h-11 w-11 place-items-center rounded-full text-white shadow-xl transition-transform hover:scale-110" style={{ background: '#25D366' }} aria-label="Contact on WhatsApp">
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.372a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" /></svg>
      </a>

      {/* scroll top */}
      <AnimatePresence>
        {showTop && (
          <motion.button onClick={navTo('hero')} className="fixed bottom-8 right-6 z-50 grid h-11 w-11 place-items-center rounded-full text-black" style={{ background: '#fff' }} aria-label="Scroll to top" initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.6 }}>
            <ChevronUp className="h-5 w-5" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

/* Intro loader: short, monochrome, skippable */
const Loader = ({ name, tagline, onSkip }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let raf;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / 1000);
      setCount(Math.round((1 - Math.pow(1 - p, 2)) * 100));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <motion.div className="fixed inset-0 z-[10000] flex cursor-pointer flex-col items-center justify-center" style={{ background: 'var(--bg)' }} onClick={onSkip} exit={{ y: '-100%' }} transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}>
      <div className="flex flex-col items-center">
        <span className="line-mask">
          <motion.span className="block font-display text-3xl font-semibold tracking-tight text-white md:text-5xl" initial={{ y: '110%' }} animate={{ y: 0 }} transition={{ duration: 0.7, ease: EASE }}>{name}</motion.span>
        </span>
        <motion.span className="mt-3 text-[11px] uppercase tracking-[0.3em]" style={{ color: 'var(--text-faint)' }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>{tagline}</motion.span>
      </div>
      <div className="absolute bottom-10 left-8 right-8 flex items-end justify-between">
        <div className="font-display text-5xl font-semibold tabular-nums text-white md:text-6xl">{count}</div>
        <div className="font-mono text-xs" style={{ color: 'var(--text-faint)' }}>tap to skip</div>
      </div>
      <div className="absolute bottom-0 left-0 h-px origin-left" style={{ width: '100%', background: 'rgba(255,255,255,0.6)', transform: `scaleX(${count / 100})` }} />
    </motion.div>
  );
};

export default Portfolio;
